import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  doSetupBillingAdminUpdateDiscount,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual, toInteger } from 'lodash';
import { BillingDiscount, BillingDiscountType } from 'types/Billing';
import { TextField, TextFieldSpecial } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { useAlert } from 'hooks/useAlert';
import {
  dateStringToNumber,
  formatDate,
  numberToDateString,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import { smartText } from 'lib/str';
import { addMonths } from 'date-fns';
import { DateField } from 'components/Field/DateField';
import { selectBillingDiscount } from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { PageHeader } from 'components/PageHeader';

const DefaultItem: BillingDiscount = {
  id: '',
  name: '',
  cost: 0,
  remainingCost: 0,
  percent: 0,
  type: BillingDiscountType.percent,
  invoiceCodes: '',
  expiryDate: 0,
};

export const BillingDiscountModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsBillingDiscount>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { itemId } = route.params;

  const defaultItem = React.useMemo(() => {
    return {
      ...DefaultItem,
      expiryDate: dateStringToNumber(
        formatDate(addMonths(new Date(), 1), 'yyyy-MM-01'),
      ),
    };
  }, []);

  const discount =
    useSelector((state) => selectBillingDiscount(state, itemId || '-1')) ||
    defaultItem;

  const dataRef = React.useRef<BillingDiscount>(cloneDeep(discount));
  const isDirty = React.useCallback(
    () => !isEqual(discount, dataRef.current),
    [discount],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    const errors: string[] = [];
    if (!dataRef.current.name.trim()) {
      errors.push('Name is required');
    }
    if (!dataRef.current.percent) {
      errors.push('Percent is required and must be more than 0%');
    }
    if (errors.length) {
      alert({
        text: errors.join('. '),
      });
      return;
    }
    dispatch(doSetupBillingAdminUpdateDiscount(dataRef.current));
  }, [alert, dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingDiscount])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingDiscount]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Discount"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Name">
        <TextField
          placeholder="Discount Name"
          onChange={(value) => handleChangeValue('name', value)}
          value={dataRef.current.name}
          onEsc={handleClose}
          autoFocus={!dataRef.current.id}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>

      {!itemId ? (
        <Card
          label="Discount Amount"
          explanation="The discount will continue until the expiry date is reached. The percentage cannot be changed after saving"
        >
          <TextField
            key="percent"
            textRight="%"
            placeholder="0"
            onChange={(value) =>
              handleChangeValue('percent', toInteger(value || '0'))
            }
            value={
              dataRef.current.percent !== 0 ? `${dataRef.current.percent}` : ''
            }
            special={TextFieldSpecial.number}
            maxNumberValue={100}
            onEsc={handleClose}
            textRightStyle={{
              paddingRight: theme.padding,
            }}
          />
        </Card>
      ) : (
        <Card label="Discount Amount">
          <CardRow value={`${dataRef.current.percent}%`} />
        </Card>
      )}

      <Card
        label="Invoice Codes"
        explanation={smartText(
          'Apply this discount to invoices with specific invoice codes. ' +
            'Comma separate for multiple codes. ' +
            'Use "default" to apply to invoices without a code',
        )}
      >
        <TextField
          placeholder="all"
          onChange={(value) => handleChangeValue('invoiceCodes', value)}
          value={dataRef.current.invoiceCodes}
          onEsc={handleClose}
        />
      </Card>

      <Card
        label="Expires After"
        explanation={smartText(
          'Discounts are applied to invoices, not individual items. ' +
            'Invoices created ≤ this date will include the discount',
        )}
      >
        <DateField
          onChange={(value) =>
            handleChangeValue('expiryDate', dateStringToNumber(value), true)
          }
          value={numberToDateString(dataRef.current.expiryDate)}
          clearable={false}
        />
      </Card>
    </Page>
  );
};
